import { useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { Tag } from "@nova/ds-component-tag"
import { PilotTrackingTagMissionSpec } from "@nova/schemas"
import { MissionTagDropdownPanel, MissionTagDropdownPanelRef } from "./MissionTagDropdownPanel"
import { useBatchTagMissions } from "../tags/TagQueries"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectTagDropdownFlightId } from "../redux/missions-table-selectors"
import { setTagDropdownFlightId } from "../redux/missions-table-slice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import classNames from "classnames"

type Props = {
    flightId: string
    missions: { missionId: string; tagIds: string[] }[]
    currentTags: { id: string; name: string }[]
    onMutate: () => void
}

const PANEL_MAX_HEIGHT = 310
export const MissionTagDropdown = ({ flightId, missions, currentTags, onMutate }: Props) => {
    const portalId = `mission-tag-dropdown-${flightId}`
    const triggerRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<MissionTagDropdownPanelRef>(null)
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const tagDropdownFlightId = useAppSelector(selectTagDropdownFlightId)
    const isOpen = tagDropdownFlightId === flightId
    const [dropdownPos, setDropdownPos] = useState<
        | { placement: "below"; top: number; left: number }
        | { placement: "above"; bottom: number; left: number }
        | null
    >(null)
    const { batchTagMissions } = useBatchTagMissions()

    const handleCommit = useCallback(
        async (
            additions: PilotTrackingTagMissionSpec[],
            deletions: PilotTrackingTagMissionSpec[]
        ) => {
            dispatch(setTagDropdownFlightId(null))
            if (additions.length || deletions.length) {
                await batchTagMissions({ additions, deletions })
                onMutate()
            }
        },
        [batchTagMissions, dispatch, onMutate]
    )

    const handleClickAway = useCallback(() => {
        if (isOpen) {
            panelRef.current?.commit()
        }
    }, [isOpen])

    const handleClose = useCallback(() => {
        dispatch(setTagDropdownFlightId(null))
    }, [dispatch])

    const handleOpen = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            if (spaceBelow < PANEL_MAX_HEIGHT) {
                setDropdownPos({
                    placement: "above",
                    bottom: window.innerHeight - rect.top + 4,
                    left: rect.left,
                })
            } else {
                setDropdownPos({ placement: "below", top: rect.bottom + 4, left: rect.left })
            }
        }
        dispatch(setTagDropdownFlightId(flightId))
    }, [dispatch, flightId])

    if (!isEditor) {
        return (
            <div className="flex gap-1 items-center flex-wrap max-w-[350px] min-w-[180px] min-h-[30px]">
                {currentTags.map((tag) => (
                    <Tag key={tag.id}>
                        <small className="text-sm">{tag.name}</small>
                    </Tag>
                ))}
            </div>
        )
    }

    return (
        <ClickAwayListenerV2 onClickAway={handleClickAway} portalId={portalId}>
            <div
                ref={triggerRef}
                className={classNames(
                    "flex gap-1 items-center flex-wrap max-w-[350px] min-w-[180px] min-h-[30px] cursor-pointer rounded transition-colors",
                    {
                        "group-hover/mission-row:bg-white-100": !currentTags.length,
                        "bg-white-100": !currentTags.length && isOpen,
                    }
                )}
                onClick={handleOpen}
            >
                {!currentTags.length && (
                    <small
                        className={classNames(
                            "group-hover/mission-row:opacity-100 transition-opacity text-white-400 pl-2",
                            {
                                "opacity-0": !isOpen,
                                "opacity-100": isOpen,
                            }
                        )}
                    >
                        Add tags
                    </small>
                )}
                {currentTags.map((tag) => (
                    <Tag key={tag.id}>
                        <small className="text-sm">{tag.name}</small>
                    </Tag>
                ))}
            </div>
            {isOpen &&
                dropdownPos &&
                createPortal(
                    <div
                        id={portalId}
                        style={{
                            position: "fixed",
                            left: dropdownPos.left,
                            ...(dropdownPos.placement === "below"
                                ? { top: dropdownPos.top }
                                : { bottom: dropdownPos.bottom }),
                        }}
                        className="z-50"
                    >
                        <MissionTagDropdownPanel
                            ref={panelRef}
                            missions={missions}
                            onCommit={handleCommit}
                            onRequestClose={handleClose}
                        />
                    </div>,
                    document.getElementById("portal")!
                )}
        </ClickAwayListenerV2>
    )
}
